import React, { useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import UnavailableNote from "./common/UnavailableNote";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

// Provider-specific "how do I generate this token" help, shown from the icon next to the PAT field.
const PAT_HELP = {
  GitHub: {
    title: "Generate a GitHub token",
    steps: [
      <>Go to <strong>GitHub → Settings → Developer settings → Fine-grained personal access tokens → Generate new token</strong>.</>,
      <>Set <strong>Resource owner</strong> to whoever owns the repositories: your GitHub <strong>organization</strong>, or <strong>your own account</strong> for personal repos.</>,
      <>Set <strong>Repository access</strong> to <strong>All repositories</strong>.</>,
      <>Under <strong>Repository permissions</strong>, grant <strong>Contents: Read-only</strong> and <strong>Metadata: Read-only</strong>.</>,
      <>Generate the token and paste it here — it is never shown again after saving.</>,
    ],
    note: "Org-owned tokens may need admin approval before they can see anything (Organization settings → Personal access tokens → Pending requests). A classic PAT with the \"repo\" scope also works but grants broader access — prefer fine-grained.",
  },
  GitLab: {
    title: "Generate a GitLab token",
    steps: [
      <>Go to <strong>GitLab → your avatar → Edit profile → Access tokens → Add new token</strong> (or use a <strong>Group access token</strong> under Group → Settings → Access tokens).</>,
      <>Select the <strong>read_api</strong> scope — it covers project listing and repository file reads, private projects included.</>,
      <>Set an expiration that fits your rotation policy, create the token, and paste it here.</>,
    ],
    note: "For self-hosted GitLab, create the token on your instance and set the Base URL field above.",
  },
  AzureDevOps: {
    title: "Generate an Azure DevOps token",
    steps: [
      <>Go to <strong>Azure DevOps → User settings (top right) → Personal access tokens → New Token</strong>.</>,
      <>Set <strong>Organization</strong> to the organization you're connecting.</>,
      <>Grant <strong>Code: Read</strong> — enough for AI discovery and secrets scanning.</>,
      <>If this connection will also power the <strong>Azure DevOps SDLC governance</strong> card, additionally grant read on <strong>Project and Team, Build, Service Connections, and Advanced Security</strong>.</>,
      <>Create the token and paste it here — it is never shown again after saving.</>,
    ],
    note: "Azure DevOps PATs always expire (max ~1 year) — set a rotation reminder. For Azure DevOps Server, set the Base URL field to your collection URL.",
  },
};

function PatHelpPopover({ provider, onClose }) {
  const help = PAT_HELP[provider] || PAT_HELP.GitHub;
  return (
    <div className="absolute left-0 top-6 z-20 w-96 max-w-[90vw] bg-white border border-gray-200 rounded-xl shadow-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-800">{help.title}</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <ol className="list-decimal list-inside space-y-1.5 text-xs text-gray-600">
        {help.steps.map((step, i) => <li key={i}>{step}</li>)}
      </ol>
      <p className="mt-2 text-[11px] text-gray-400">{help.note}</p>
    </div>
  );
}

// Source Code Connections panel (IntegrationSettings → Security Intelligence).
// Manages org-level GitHub/GitLab connections (target + per-connection PAT) consumed by
// Secrets Scanning targets and AI Asset Discovery. Tokens are write-only.
const SourceCodeConnectionsPanel = ({ connections, onChanged, canManage }) => {
  const apiCall = useApi();
  // Restricted / AirGapped: GitHub, GitLab and Azure DevOps are unreachable, so Add and Test are
  // disabled; listing, Edit/Save and Delete only change saved data and stay enabled.
  const { isModuleAvailable } = useModuleAvailability();
  const unavailable = !isModuleAvailable("secretsScanning");

  const emptyForm = { provider: "GitHub", target: "", baseUrl: "", displayName: "", token: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState({}); // id -> {ok, message}
  const [testingId, setTestingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showPatHelp, setShowPatHelp] = useState(false);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const startEdit = (conn) => {
    setEditingId(conn.id);
    setError(null);
    setForm({
      provider: conn.provider,
      target: conn.target,
      baseUrl: conn.baseUrl || "",
      displayName: conn.displayName || "",
      token: "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); setError(null); };

  const handleSave = async () => {
    setError(null);
    if (!form.target.trim()) { setError("Target (GitHub org or GitLab group) is required."); return; }
    if (!editingId && !form.token.trim()) { setError("A Personal Access Token is required for a new connection."); return; }
    setSaving(true);
    try {
      const payload = {
        provider: form.provider,
        target: form.target.trim(),
        baseUrl: form.provider !== "GitHub" && form.baseUrl.trim() ? form.baseUrl.trim() : null,
        displayName: form.displayName.trim() || null,
        token: form.token.trim() || null,
      };
      if (editingId) {
        await apiCall("PUT", `/source-code-connections/${editingId}`, payload);
      } else {
        await apiCall("POST", "/source-code-connections", payload);
      }
      cancelEdit();
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save connection.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (conn) => {
    setTestingId(conn.id);
    try {
      const result = await apiCall("POST", `/source-code-connections/${conn.id}/test`);
      setTestResults(prev => ({ ...prev, [conn.id]: result }));
    } catch {
      setTestResults(prev => ({ ...prev, [conn.id]: { ok: false, message: "Test failed — check backend logs." } }));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiCall("DELETE", `/source-code-connections/${deleteTarget.id}`);
      setDeleteTarget(null);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to delete connection.");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const label = (conn) => conn.displayName || `${conn.provider} / ${conn.target}`;

  return (
    <div className="space-y-4">
      {unavailable && <UnavailableNote />}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700">
        Org-level <strong>GitHub / GitLab connections</strong> with their own access tokens. Secrets Scanning targets
        and <strong>AI Asset Discovery</strong> use these connections to read your repositories.
        For AI discovery the token needs <strong>private-repo read access</strong> (GitHub fine-grained PAT:
        Contents &amp; Metadata read on all repositories; GitLab: <code>read_api</code>).
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{error}</div>
      )}

      {/* Existing connections */}
      {connections.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-700">Connections</span>
          </div>
          <div className="divide-y divide-gray-100">
            {connections.map(conn => (
              <div key={conn.id} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    conn.provider === "GitHub" ? "bg-gray-900 text-white"
                      : conn.provider === "AzureDevOps" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
                    {conn.provider === "AzureDevOps" ? "Azure DevOps" : conn.provider}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800 truncate">{label(conn)}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {conn.target}{conn.baseUrl ? ` · ${conn.baseUrl}` : ""} · {conn.tokenSet ? "token saved" : "no token"}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleTest(conn)} disabled={unavailable || testingId === conn.id}
                        className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                        {testingId === conn.id ? "Testing…" : "Test"}
                      </button>
                      <button onClick={() => startEdit(conn)}
                        className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                        Edit
                      </button>
                      <button onClick={() => setDeleteTarget(conn)}
                        className="text-xs px-2.5 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {testResults[conn.id] && (
                  <div className={`mt-2 text-xs px-2 py-1.5 rounded-lg border ${testResults[conn.id].partial
                    ? "bg-amber-50 border-amber-200 text-amber-700"
                    : testResults[conn.id].ok
                    ? "bg-green-50 border-green-200 text-green-700"
                    : "bg-red-50 border-red-200 text-red-700"}`}>
                    {testResults[conn.id].message}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add / edit form — no overflow-hidden: the PAT help popover extends past the card edge */}
      {canManage && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              {editingId ? "Edit Connection" : "Add Connection"}
            </span>
            {editingId && (
              <button onClick={cancelEdit} className="text-xs text-gray-400 hover:text-gray-600">Cancel edit</button>
            )}
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
              <select value={form.provider} onChange={e => updateForm("provider", e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="GitHub">GitHub</option>
                <option value="GitLab">GitLab</option>
                <option value="AzureDevOps">Azure DevOps</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {form.provider === "GitHub" ? "Organization or username"
                  : form.provider === "AzureDevOps" ? "Organization name" : "Group path"} <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.target} onChange={e => updateForm("target", e.target.value)}
                placeholder={form.provider === "GitHub" ? "my-org or my-username"
                  : form.provider === "AzureDevOps" ? "my-ado-org" : "my-group or my-group/sub"}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            {form.provider !== "GitHub" && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {form.provider === "GitLab" ? "Base URL (self-hosted only)" : "Base URL (Azure DevOps Server only)"}
                </label>
                <input type="text" value={form.baseUrl} onChange={e => updateForm("baseUrl", e.target.value)}
                  placeholder={form.provider === "GitLab"
                    ? "https://gitlab.example.com (leave empty for gitlab.com)"
                    : "https://azdo.example.com/MyCollection (leave empty for dev.azure.com)"}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display name (optional)</label>
              <input type="text" value={form.displayName} onChange={e => updateForm("displayName", e.target.value)}
                placeholder="e.g. Engineering GitHub"
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <div className="relative flex items-center gap-1.5 mb-1">
                <label className="text-xs font-medium text-gray-600">
                  Personal Access Token {editingId ? "" : <span className="text-red-500">*</span>}
                </label>
                <button type="button" onClick={() => setShowPatHelp(v => !v)}
                  className="text-gray-400 hover:text-indigo-600" aria-label="How to generate this token"
                  title="How to generate this token">
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
                {showPatHelp && <PatHelpPopover provider={form.provider} onClose={() => setShowPatHelp(false)} />}
              </div>
              <input type="password" value={form.token} onChange={e => updateForm("token", e.target.value)}
                placeholder={editingId ? "••••••••••••••• (saved — enter new to replace)" : "Paste PAT"}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
              <p className="text-xs text-gray-400 mt-1">
                Never displayed again. AI discovery reads all repositories the token can
                see — including private ones.
              </p>
            </div>
          </div>
          <div className="px-4 pb-4">
            <button onClick={handleSave} disabled={saving || (unavailable && !editingId)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: "#111827" }}>
              {saving ? "Saving…" : editingId ? "Save Changes" : "Add Connection"}
            </button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isBusy={deleting}
        requireTyping={false}
        confirmLabel="Delete"
        title="Delete Source Code Connection"
        message={deleteTarget
          ? `This will delete the connection "${label(deleteTarget)}" and its stored token, and cascade:\n\n`
            + `• Its Secrets Scan targets are removed, findings from its repositories are permanently deleted, and their risks are removed.\n`
            + (deleteTarget.provider === "AzureDevOps"
                ? `• If the Azure DevOps SDLC governance card uses this connection, it is disabled and its controls and risks are removed.\n`
                : "")
            + `• AI Intelligence assets discovered through this connection go stale on the next scan and their posture risks close.`
          : ""}
      />
    </div>
  );
};

export default SourceCodeConnectionsPanel;
